import { startOfNextDay, userTimezone } from '@unimpaired/utils'
import {
  addDays,
  addHours,
  compareDesc,
  differenceInCalendarDays,
  differenceInDays,
  eachHourOfInterval,
  endOfDay,
  endOfHour,
  getHours,
  interval,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  isWithinInterval,
  max,
  min,
  set,
  startOfDay,
  startOfHour,
  subDays,
  subHours,
  subMilliseconds,
  subMinutes,
} from 'date-fns'
import { addHours as addHoursC } from 'date-fns/fp'
import { pipe } from 'fp-ts/lib/function'
import * as R from 'ramda'
import {
  DayScheduleEventDescriptor,
  DayScheduleProps,
  ScheduleEvent,
} from './DaySchedule.types'
import { tz, TZDate } from '@date-fns/tz'

/**
 * Wrap each event's start/endTime in a TZDate for the given timezone and
 * drop events with an invalid date. Every schedule component calls this on
 * its `events` prop so all downstream date math runs in the intended TZ.
 *
 * Fast path: when the events are already TZDate-wrapped in the target
 * timezone (the typical case for downstream components — MultidaySchedule
 * normalizes once and hands the result to DaySchedule, which hands it to
 * HourlySchedule), skip the per-event spread + double TZDate allocation
 * and return the input as-is when all events are valid. This trims ~2×
 * the event-count worth of TZDate constructions out of every render of
 * the schedule tree.
 */
export const normalizeEventsToTimezone = <T extends ScheduleEvent>(
  events: T[],
  timezone: string | undefined,
): T[] => {
  if (events.length === 0) return events
  const first = events[0]
  const alreadyNormalized =
    first.startTime instanceof TZDate &&
    first.endTime instanceof TZDate &&
    (first.startTime as TZDate).timeZone === timezone &&
    (first.endTime as TZDate).timeZone === timezone
  if (alreadyNormalized) {
    // Preserve the input reference when nothing needs filtering — keeps
    // upstream memo identity stable.
    const allValid = events.every(
      (event) => isValid(event.startTime) && isValid(event.endTime),
    )
    if (allValid) return events
    return events.filter(
      (event) => isValid(event.startTime) && isValid(event.endTime),
    )
  }
  return events
    .map((event) => ({
      ...event,
      startTime: new TZDate(event.startTime, timezone),
      endTime: new TZDate(event.endTime, timezone),
    }))
    .filter((event) => isValid(event.startTime) && isValid(event.endTime))
}

/**
 * `date-fns` `differenceInHours` returns the true duration, respecting DST
 * -> `differenceInHours(0300, 0000) === 4` on a spring-forward day
 * but the schedule cares about visual hour lines ('wall clock' hours)
 * -> `differenceInWallClockHours(0300, 0000) === 3`
 */
export const differenceInWallClockHours = (
  laterDate: Date | TZDate,
  earlierDate: Date | TZDate,
) => {
  const daysBetweenDates = differenceInCalendarDays(laterDate, earlierDate)
  if (daysBetweenDates === 0) {
    return getHours(laterDate) - getHours(earlierDate)
  }
  const hoursRemainingInFirstDay = 24 - getHours(earlierDate)
  return (
    hoursRemainingInFirstDay + (daysBetweenDates - 1) * 24 + getHours(laterDate)
  )
}

/**
 * Is the event on the schedule day, accounting for the schedule's midnight
 * offset (e.g. if midnightOffset=4, 2am on the next calendar day still
 * belongs to this schedule day).
 */
export const isSameScheduleDay = (
  scheduleDay: Date,
  event: { startTime: Date; endTime: Date },
  midnightOffset: number | undefined,
) =>
  isSameDay(subHours(event.startTime, midnightOffset || 0), scheduleDay) ||
  isSameDay(subHours(event.endTime, midnightOffset || 0), scheduleDay)

/**
 * Set the date to the start of the specified hour. Defaults to today if
 * date is undefined.
 *
 * @param currentDate Anchor used when `date` is undefined. Defaulted to
 *   `new Date()`; pass explicitly to keep this pure for tests.
 */
export const setDateToHour = (
  date: Date | undefined,
  hour: number,
  timezone?: string,
  currentDate: Date = new Date(),
) => {
  const options = { in: tz(timezone ?? userTimezone) }
  return set(
    date ?? currentDate,
    {
      hours: hour,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    },
    options,
  )
}

/**
 * When DST springs forward, `eachHourOfInterval` skips the missing hour,
 * leaving a gap in the hour labels (e.g. `[1, 3]`). This re-inserts the
 * skipped hour so the schedule visual has no missing row.
 */
export const addMissingDaylightSavingsHours = (hours: number[]) =>
  hours.reduce<number[]>((acc, cur) => {
    if (acc.length === 0) return [cur]
    const hourFollowingPrevious = (acc[acc.length - 1] + 1) % 24
    if (hourFollowingPrevious !== cur)
      return [...acc, hourFollowingPrevious, cur]
    return [...acc, cur]
  }, [])

/**
 * Drop any event that ends before `interval[0]` or starts after `interval[1]`.
 * Either boundary may be omitted to disable that check.
 */
export const rejectEventsOutsideInterval =
  (interval: [Date | undefined, Date | undefined]) =>
  <T extends ScheduleEvent>(events: T[]): T[] =>
    pipe(
      events,
      R.when<T[], T[]>(
        R.always(R.isNotNil(interval[0])),
        R.reject<T>((event) => !isAfter(event.endTime, interval[0]!)),
      ),
      R.when<T[], T[]>(
        R.always(R.isNotNil(interval[1])),
        R.reject<T>((event) => isAfter(event.startTime, interval[1]!)),
      ),
    )

/**
 * Resolve the `eventDescriptor` prop into a `{singular, plural}` pair used
 * in UI strings ("Create Class", "No Events", etc.).
 */
export const getDefaultEventDescriptor = (
  descriptor: DayScheduleEventDescriptor,
) => {
  if (!descriptor) return { singular: 'event', plural: 'events' }
  if (Array.isArray(descriptor))
    return { singular: descriptor[0], plural: descriptor[1] }
  return { singular: descriptor, plural: descriptor + 's' }
}

type StartFromProps = Pick<
  DayScheduleProps<ScheduleEvent>,
  'pageDate' | 'midnightOffset' | 'defaultPageStartMidnightOffset' | 'timezone'
>

/**
 * Compute the configured start-of-day or end-of-day for the schedule,
 * offset by midnightOffset. Returns undefined when no pageDate is supplied
 * (caller has no anchor day to offset from).
 *
 * Note: both boundaries intentionally use `defaultPageStartMidnightOffset`
 * (not `...End...`) as the fallback — preserves pre-existing behavior
 * where midnightOffset is the single source of truth for both ends.
 *
 * TODO: endOfDay + addHours is not DST-aware in the fall (adds duration
 * hours, not wall-clock hours)
 */
const getDayBoundaryFrom = (
  {
    midnightOffset,
    defaultPageStartMidnightOffset = 0,
    ...props
  }: StartFromProps,
  boundary: 'start' | 'end',
) => {
  if (!props.pageDate) return undefined
  const pageDate = new TZDate(props.pageDate, props.timezone)
  const anchor = boundary === 'start' ? startOfDay(pageDate) : endOfDay(pageDate)
  const offset = midnightOffset ?? defaultPageStartMidnightOffset ?? 0
  return offset === 0 ? anchor : addHours(anchor, offset)
}

export const getDayStartFrom = (props: StartFromProps) =>
  getDayBoundaryFrom(props, 'start')

export const getDayEndFrom = (props: StartFromProps) =>
  getDayBoundaryFrom(props, 'end')

/**
 * Compute the schedule day's visual end timestamp. Takes the latest event
 * end, rounds to the end of its hour, and clamps against the manual page
 * end when supplied so events don't bleed into the next day.
 */
export const getEndDayAt = <T extends ScheduleEvent>(
  props: Pick<
    DayScheduleProps<T>,
    | 'timezone'
    | 'events'
    | 'pageDate'
    | 'midnightOffset'
    | 'defaultPageEndMidnightOffset'
    | 'defaultPageStartMidnightOffset'
  >,
  currentDate: Date = new Date(),
) => {
  const dayStartFromProps = getDayStartFrom(props)
  const dayEndFromProps = getDayEndFrom(props)
  const pageDate = new TZDate(props.pageDate || currentDate, props.timezone)
  return pipe(
    props.events,
    rejectEventsOutsideInterval([dayStartFromProps, dayEndFromProps]),
    R.map(R.prop('endTime')),
    (events) => {
      // no events: end at the specified offset from the end of day, or 21:00
      if (events.length === 0)
        return props.defaultPageEndMidnightOffset
          ? addHours(
              startOfNextDay(pageDate),
              props.defaultPageEndMidnightOffset,
            )
          : setDateToHour(pageDate, 21)
      return (
        events.sort(compareDesc)[0] || new TZDate(currentDate, props.timezone)
      )
    },
    // sub one minute so events ending on the hour (11:00) don't add an
    // extra hour line, but events in the middle (11:15) do
    (time) => subMinutes(time, 1),
    endOfHour,
    // If the latest event ends after the manual pageDate end...
    R.ifElse(
      (latestEventEndtime) =>
        !!dayEndFromProps && isAfter(latestEventEndtime, dayEndFromProps),
      // ...use the manual end so we don't render into the next day
      R.always(dayEndFromProps!),
      // otherwise honor defaultPageEndMidnightOffset if supplied
      R.ifElse(
        R.always(props.defaultPageEndMidnightOffset !== undefined),
        (latestTime: Date) =>
          pipe(
            endOfDay(pageDate ?? latestTime),
            addHoursC(
              Math.max(
                // Note: the Math.min with props.midnightOffset isn't fully
                // thought through. It fixes the case where
                // defaultPageStartMidnightOffset pushed beyond the day-end
                // cutoff as determined by midnightOffset. Likely breaks
                // for schedules significantly longer than 24 hours.
                Math.min(
                  props?.midnightOffset || 0,
                  props.defaultPageEndMidnightOffset!,
                ),
                differenceInWallClockHours(
                  endOfHour(latestTime),
                  endOfDay(pageDate ?? latestTime),
                ),
              ),
            ),
          ),
        R.identity,
      ),
    ),
  )
}

/**
 * Compute the schedule day's visual start timestamp. Takes the earliest
 * event start, rounds to its hour, and clamps against the manual page
 * start when supplied so events don't bleed into the prior day.
 */
export const getStartDayAt = <T extends ScheduleEvent>(
  props: Pick<
    DayScheduleProps<T>,
    | 'timezone'
    | 'events'
    | 'pageDate'
    | 'midnightOffset'
    | 'defaultPageEndMidnightOffset'
    | 'defaultPageStartMidnightOffset'
  >,
  currentDate: Date = new Date(),
) => {
  const dayStartFromProps = getDayStartFrom(props)
  const dayEndFromProps = getDayEndFrom(props)
  const pageDate = new TZDate(props.pageDate || currentDate, props.timezone)
  return pipe(
    props.events,
    rejectEventsOutsideInterval([dayStartFromProps, dayEndFromProps]),
    R.map(R.prop('startTime')),
    R.filter(isValid),
    (dates) => {
      if (dates.length === 0)
        // no events: start at the spec'd offset or pageDate's hour
        return props.defaultPageStartMidnightOffset
          ? addHours(startOfDay(pageDate), props.defaultPageStartMidnightOffset)
          : pageDate
      return min(dates)
    },
    (earliestEvent) =>
      set(earliestEvent, {
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      }),
    // If the earliest event starts before the manual pageDate start...
    R.ifElse(
      (earliestEvent) =>
        !!dayStartFromProps && isBefore(earliestEvent, dayStartFromProps),
      // ...use the manual start so we don't render into the prior day
      R.always(dayStartFromProps!),
      // otherwise compute the offset from start of day of the earliest event
      R.ifElse(
        R.always(props.defaultPageStartMidnightOffset !== undefined),
        (earliestTime: Date) =>
          pipe(
            startOfDay(pageDate ?? earliestTime),
            // use either the forced offset or the earliest event's hour,
            // whichever is earlier
            addHoursC(
              Math.min(
                props.defaultPageStartMidnightOffset!,
                differenceInWallClockHours(
                  earliestTime,
                  startOfDay(pageDate ?? earliestTime),
                ),
              ),
            ),
          ),
        R.identity,
      ),
    ),
  )
}

/**
 * Build the array of hour labels ('12AM', '1AM'...) between the computed
 * startDayAt and endDayAt, inserting any hour skipped by DST.
 */
export const getDayScheduleHours = <T extends ScheduleEvent>(
  props: Pick<
    DayScheduleProps<T>,
    | 'events'
    | 'pageDate'
    | 'midnightOffset'
    | 'defaultPageEndMidnightOffset'
    | 'defaultPageStartMidnightOffset'
  >,
  currentDate: Date = new Date(),
) => {
  const startDayAt = getStartDayAt(props, currentDate)
  const endDayAt = getEndDayAt(props, currentDate)

  return pipe(
    interval(startDayAt, subMilliseconds(endDayAt, 1)),
    eachHourOfInterval,
    R.map(getHours),
    // Only relevant when the schedule spans a DST jump (authored 3/8/2025
    // when DST was the next day — left in so the bug won't resurface).
    addMissingDaylightSavingsHours,
    R.map((hour) => hour % 24),
    R.map((hour) => {
      if (hour === 0) return '12AM'
      if (hour === 12) return '12PM'
      if (hour > 12) return hour - 12 + 'PM'
      return hour + 'AM'
    }),
  )
}

/**
 * Return the start of the earliest event on or after `fromDate`, accounting
 * for the schedule's midnight offset. Returns undefined if no such event.
 */
export const getNextDayWithEvent = (props: {
  fromDate: Date
  events: { startTime: Date; endTime: Date }[]
  midnightOffset: number
}) =>
  pipe(
    props.events,
    R.sort(R.ascend(R.prop('startTime'))),
    R.find((event) =>
      isAfter(
        event.endTime,
        addHours(startOfDay(props.fromDate), props.midnightOffset),
      ),
    ),
    (event) => (!!event ? event.startTime : undefined),
  )

/**
 * Find a day's events matching a predicate (via `find` or `findLast`),
 * then return the earliest startTime from that day. Shared core for the
 * `findDateOfFirstDayWithEvents*` helpers below.
 */
const earliestStartFromMatchingDay =
  <T extends ScheduleEvent>(
    strategy: 'find' | 'findLast',
    dayHasMatch: (events: T[]) => boolean,
  ) =>
  (eventsByDay: T[][]): Date | undefined => {
    const pickDay = strategy === 'findLast' ? R.findLast : R.find
    const matchedDay = pickDay(dayHasMatch, eventsByDay)
    if (!matchedDay?.length) return undefined
    return min(matchedDay.map((e) => e.startTime))
  }

export const findDateOfFirstDayWithEventsAfter =
  (afterOrOnDate: TZDate) =>
  <T extends ScheduleEvent>(eventsByDay: T[][]) => {
    const startOfDayOfDateToCompare = startOfDay(afterOrOnDate)
    return earliestStartFromMatchingDay<T>('find', (events) =>
      events.some(({ startTime }) =>
        isAfter(startTime, startOfDayOfDateToCompare),
      ),
    )(eventsByDay)
  }

export const findDateOfFirstDayWithEventsClosestTo =
  (closestToDate: TZDate, midnightOffset: number) =>
  <T extends ScheduleEvent>(eventsByDay: T[][]) => {
    const endOfDayOfDateToCompare = addHours(
      endOfDay(closestToDate),
      midnightOffset,
    )
    return earliestStartFromMatchingDay<T>('findLast', (events) =>
      events.some(({ startTime }) =>
        isBefore(startTime, endOfDayOfDateToCompare),
      ),
    )(eventsByDay)
  }

/**
 * For each day's events, find the hour offset (from start-of-day) of the
 * latest end time, and return the difference between that and the next
 * midnight — expressed as a negative number of hours. Exported for
 * external consumers; not currently used in this module.
 */
export const getMidnightOffsetOfLatestEvent = (
  eventsByDay: { day: Date; events: ScheduleEvent[] }[],
) =>
  pipe(
    eventsByDay,
    R.map(({ day, events: eventsOnDay }) =>
      pipe(eventsOnDay, R.map(R.prop('endTime')), max, endOfHour, (endOfHour) =>
        differenceInWallClockHours(endOfHour, startOfDay(day)),
      ),
    ),
    (latestHourOfEachDay) => Math.max(...latestHourOfEachDay),
    (largestHourOffsetFromStartOfDay) =>
      largestHourOffsetFromStartOfDay === 24
        ? 0
        : 24 - (largestHourOffsetFromStartOfDay % 24),
    (hoursTilNextMidnight) => -1 * hoursTilNextMidnight,
  )

/**
 * For each day's events, find the hour offset (from start-of-day) of the
 * earliest start time, and return the minimum across days. Exported for
 * external consumers; not currently used in this module.
 */
export const getMidnightOffsetOfEarliestEvent = (
  eventsByDay: { day: Date; events: ScheduleEvent[] }[],
) =>
  pipe(
    eventsByDay,
    R.map(({ day, events: eventsOnDay }) =>
      pipe(
        eventsOnDay,
        R.map(R.prop('startTime')),
        min,
        startOfHour,
        (startOfHour) =>
          differenceInWallClockHours(startOfHour, startOfDay(day)),
      ),
    ),
    (earliestHourOfEachDay) => Math.min(...earliestHourOfEachDay),
  )

const offsetDayToInterval = (numHours: number) => (date: Date) =>
  pipe(
    date,
    (date) => addHours(date, numHours),
    (shiftedStartDate) =>
      interval(shiftedStartDate, addDays(shiftedStartDate, 1)),
  )

const getStartOfFirstEvent = (events: ScheduleEvent[]) =>
  pipe(events, R.map(R.prop('startTime')), min)

const getEndOfLastEvent = (events: ScheduleEvent[]) =>
  pipe(
    events,
    R.map(R.prop('endTime')),
    max,
    // sub a minute so events with end 00:00:21 aren't considered as
    // existing for 21 seconds on the next day
    (event) => subMinutes(event, 1),
  )

const getLastDayOfEvents =
  (options: { midnightOffset: number }) => (events: ScheduleEvent[]) => {
    const midnightOffset = options.midnightOffset ?? 0
    const endOfLastEvent = getEndOfLastEvent(events)

    return isAfter(
      endOfLastEvent,
      addHours(endOfDay(endOfLastEvent), midnightOffset),
    )
      ? addDays(endOfDay(endOfLastEvent), 1)
      : isBefore(
          endOfLastEvent,
          addHours(startOfDay(endOfLastEvent), midnightOffset),
        )
      ? subDays(startOfDay(endOfLastEvent), 1)
      : endOfDay(endOfLastEvent)
  }

const getFirstDayOfEvents =
  (options: { midnightOffset: number }) => (events: ScheduleEvent[]) => {
    const midnightOffset = options.midnightOffset
    const startOfFirstEvent = getStartOfFirstEvent(events)
    return isBefore(
      startOfFirstEvent,
      addHours(startOfDay(startOfFirstEvent), midnightOffset),
    )
      ? subDays(startOfDay(startOfFirstEvent), 1)
      : isAfter(
          startOfFirstEvent,
          addHours(endOfDay(startOfFirstEvent), midnightOffset),
        )
      ? addDays(startOfDay(startOfFirstEvent), 1)
      : startOfDay(startOfFirstEvent)
  }

/**
 * Group events by day, trimming any event that starts before or ends after
 * its day's interval. Exported for external consumers; not currently used
 * in this module.
 */
export const getEventsByDay =
  (options: { firstDay?: Date; lastDay?: Date; midnightOffset?: number }) =>
  (events: ScheduleEvent[]) => {
    const midnightOffset = options.midnightOffset ?? 0
    const firstDay =
      options.firstDay ?? getFirstDayOfEvents({ midnightOffset })(events)
    const lastDay =
      options.lastDay ?? getLastDayOfEvents({ midnightOffset })(events)
    const days = pipe(
      R.range(0, differenceInDays(lastDay, firstDay) + 1),
      R.map((dayIncrement) => addDays(firstDay, dayIncrement)),
    )

    const eventsByDay = pipe(
      days,
      R.map(offsetDayToInterval(midnightOffset)),
      (daysAsIntervals) =>
        daysAsIntervals.map((dayAsInterval, i) =>
          pipe(
            events,
            R.partition<ScheduleEvent>((event) =>
              R.or(
                isWithinInterval(event.startTime, dayAsInterval),
                isWithinInterval(
                  subMilliseconds(event.endTime, 1),
                  dayAsInterval,
                ),
              ),
            ),
            R.nth(0),
            R.defaultTo([]),
            R.map((event: ScheduleEvent) => ({
              ...event,
              startTime: isBefore(event.startTime, dayAsInterval.start)
                ? dayAsInterval.start
                : event.startTime,
              endTime: isAfter(event.endTime, dayAsInterval.end)
                ? dayAsInterval.end
                : event.endTime,
            })),
            (events) => ({ day: days[i], events }),
          ),
        ),
    )

    return eventsByDay
  }
